import React from 'react'
import { connect } from 'react-redux'
import { getCurrentSession } from '../../../lib/authentication'
import ToastMessage, { toastify } from '../../components/shared/Toasts'
import { v4 } from 'uuid'
import { setAdditionalDocumentsId } from '../actions'
import { X } from 'lucide-react'

const MAX_ATTACHMENT_SIZE_MB = 19
const MAX_ATTACHMENT_SIZE_BYTES = 1024 ** 2 * MAX_ATTACHMENT_SIZE_MB
const ACCEPT = ['.csv', '.xls', '.xlsx', '.pdf', '.docx', '.doc']
const ADDL_DOCS_EXPIRE_DAYS = 90
const INVALID_CHARS_REGEX = /[\\}{\^%`\[\]"'><~#\|]/g

const toastFileUploadLimit = (
  <ToastMessage icon="exclamation">
    Total size of files more than {MAX_ATTACHMENT_SIZE_MB}MB. <br />
    Please send the document directly to the UW.
  </ToastMessage>
)

const toastFileValidDocument = (
  <ToastMessage icon="exclamation">
    Document is not valid. <br />
    Please upload document in pdf, word or excel format.
  </ToastMessage>
)

const toastFileUploadError = (
  <ToastMessage icon="exclamation">
    Oops... Error encountered during upload. <br />
  </ToastMessage>
)

const toastDuplicateFileUpload = (
  <ToastMessage icon="exclamation">
    Cannot upload a file with a duplicate file name.
  </ToastMessage>
)

const toastDeleteSuccess = (
  <ToastMessage icon="check">
    Attachment deleted successfully.
  </ToastMessage>
)

class AdditionalDocument extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      file: [],
      hasError: false,
      isDragging: false,
      size: 0,
      selected: false,
      valid: false,
      loading: false,
      uploaded: false,
      IsUWDoc: false,
    }
    this.handleDrop = this.handleDrop.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.formatActionColumn = this.formatActionColumn.bind(this)
    this.handleRemove = this.handleRemove.bind(this)
    this.suppress = this.suppress.bind(this)
    this.handleDragOverEnter = this.handleDragOverEnter.bind(this)
    this.handleDragLeaveEnd = this.handleDragLeaveEnd.bind(this)
    this.fileSizeHandler = this.fileSizeHandler.bind(this)
    this.handleUpload = this.handleUpload.bind(this)

    this.additionalDocumentsId = props.additionalDocumentsId
      ? props.additionalDocumentsId
      : null
    this.isUnderwriter = props.isUnderwriter
  }

  async componentDidMount() {
    if (this.additionalDocumentsId) {
      try {
        const { accessToken } = await getCurrentSession()
        const response = await apigClient.submissionAddlDocsGet(
          {
            id: this.additionalDocumentsId,
          },
          {},
          { headers: { Authorization: accessToken } },
        )

        const fileArrCopy = []
        let newSize = 0
        if (Array.isArray(response.data)) {
          response.data.forEach((f) => {
            const fileName = f.key.split('/').pop()
            fileArrCopy.push({
              name: fileName,
              size: f.size,
              IsUWDoc: f.IsUWDoc
            })
            newSize += f.size
          })
        }
        // Always update state file array from s3 as thats source of truth.
        this.setState({
          file: fileArrCopy,
          size: newSize,
        })
      } catch (e) {
        console.log('Error getting Additional Document files list...')
      }
    }
  }

  fileSizeHandler(cell, row, enumObject, rowIndex) {
    const isFileLimitExceeded = row.size > MAX_ATTACHMENT_SIZE_BYTES
    if (isFileLimitExceeded) {
      return (
        <span style={{ color: 'red', fontSize: '12px' }}>
          {this.dataSizeFormatter(row.size)}
        </span>
      )
    } else {
      return <span style={{ fontSize: '12px' }}>{this.dataSizeFormatter(row.size)}</span>
    }
  }

  dataSizeFormatter(dataSize) {
    if (!dataSize) {
      return
    }
    const dataSizeUnits = ['Bytes', 'KB', 'MB']
    let index = 0
    while (index < dataSizeUnits.length - 1 && dataSize / 1024 > 1) {
      index++
      dataSize /= 1024
    }
    dataSize = Math.round(dataSize * 100) / 100
    return `${dataSize}${dataSizeUnits[index]}`
  }

  async handleRemove(name) {
    let newSize = 0
    const list = this.state.file.filter((k) => {
      if (k.name != name) {
        newSize += k.size
        return true
      }
      return false
    })
    this.setState({
      file: list,
      size: newSize,
    })
    try {
      const { accessToken } = await getCurrentSession()
      await apigClient.submissionAddlDocsDelete(
        {
          id: this.additionalDocumentsId,
          fileName: name,
        },
        {},
        { headers: { Authorization: accessToken } },
      )
      toastify.success(toastDeleteSuccess)
    } catch (e) {
      console.log('Error deleting Additional Document file...')
    }
    return
  }
  setIsDragging = (toggle = false) => {
    this.setState({ isDragging: toggle })
  }

  setHasError = (toggle = false) => {
    this.setState({ hasError: toggle })
  }
  suppress(event) {
    event.stopPropagation()
    event.preventDefault()
  }

  handleDragOverEnter = (event) => {
    this.suppress(event)
    this.setIsDragging(true)
  }

  handleDragLeaveEnd = (event) => {
    this.suppress(event)
    this.setHasError(false)
    this.setIsDragging(false)
  }

  handleDrop = (event) => {
    this.handleDragLeaveEnd(event)
    let arr = []
    let newsize = 0
    let IsUWDoc = this.isUnderwriter ? true : false
    for (let i = 0; i < event.dataTransfer.files.length; i++) {
      let ext = ''
      let sanitizedName = ''
      if (typeof event.dataTransfer.files[i].name === 'string') {
        sanitizedName = event.dataTransfer.files[i].name.replace(INVALID_CHARS_REGEX, '')
      }
      const newFile = new File([event.dataTransfer.files[i]], sanitizedName, {
        type: event.dataTransfer.files[i].type,
      })

      if (newFile.name) {
        const nameParts = newFile.name.split('.')
        ext = nameParts.length >= 2 ? `.${nameParts[nameParts.length - 1]}` : ''
      }
      if (newFile.type === '' || !ACCEPT.includes(ext)) {
        toastify.error(toastFileValidDocument)
        return
      }
      // check for uniqueness
      else if (this.state.file.find((f) => f.name === newFile.name)) {
        toastify.error(toastDuplicateFileUpload)
        return
      }
      else {
        newsize = newsize + newFile.size
        arr.push({
          name: newFile.name,
          size: newFile.size,
          IsUWDoc: IsUWDoc,
          _file: newFile,
        })
      }
    }
    if (this.state.size + newsize >= MAX_ATTACHMENT_SIZE_BYTES) {
      toastify.error(toastFileUploadLimit)
      return
    } else {
      this.setState(
        (prev) => {
          return { ...prev, file: [...prev.file, ...arr], size: prev.size + newsize }
        },
        () => this.handleUpload(arr),
      )
    }
  }

  handleClick(evt) {
    const arr = []
    let newsize = 0
    let IsUWDoc = this.isUnderwriter ? true : false
    for (let i = 0; i < evt.target.files.length; i++) {
      let sanitizedName = ''

      if (typeof evt.target.files[i].name === 'string') {
        sanitizedName = evt.target.files[i].name.replace(INVALID_CHARS_REGEX, '')
      }

      const newFile = new File([evt.target.files[i]], sanitizedName, {
        type: evt.target.files[i].type,
      })

      if (newFile.type === '') {
        toastify.error(toastFileValidDocument)

        return
      }
      // check for uniqueness
      else if (this.state.file.find((f) => f.name === newFile.name)) {
        toastify.error(toastDuplicateFileUpload)
        evt.target.value = ''
        return
      }
      else {
        newsize = newsize + newFile.size
        arr.push({
          name: newFile.name,
          size: newFile.size,
          IsUWDoc: IsUWDoc,
          _file: newFile,
        })
      }
    }

    if (this.state.size + newsize >= MAX_ATTACHMENT_SIZE_BYTES) {
      toastify.error(toastFileUploadLimit)

      return
    } else {
      this.setState(
        (prev) => ({ ...prev, file: [...prev.file, ...arr], size: prev.size + newsize }),
        () => this.handleUpload(arr),
      )
    }
  }

  async handleUpload(fileUploadArr) {
    let additionalDocumentsId = null
    if (this.additionalDocumentsId == null) {
      additionalDocumentsId = v4()
    } else {
      additionalDocumentsId = this.additionalDocumentsId
    }

    const failedUploadsMap = new Map()

    this.setState(
      {
        loading: true,
      },
      async () => {
        try {
          await Promise.allSettled(
            fileUploadArr.map(async (item) => {
              const file = item._file
              try {
                const { accessToken } = await getCurrentSession()
                const response = await apigClient.preSignedURLAddlDocPut(
                  {
                    fileName: file.name,
                    id: additionalDocumentsId,
                  },
                  {},
                  { headers: { Authorization: accessToken } },
                )
                if (response.data && response.data.url) {
                  const response2 = await fetch(response.data.url, {
                    method: 'put',
                    body: file,
                  })
                  if (!response2.ok) throw new Error('Error uploading file...')
                } else {
                  throw new Error('Missing upload url...')
                }
              } catch (e) {
                failedUploadsMap.set(file.name, file.name)
                throw new Error(e)
              }
            }),
          )
          if (this.additionalDocumentsId == null && additionalDocumentsId != null) {
            this.props.dispatch(setAdditionalDocumentsId(additionalDocumentsId))
            this.additionalDocumentsId = additionalDocumentsId
          }

          if (fileUploadArr.length > failedUploadsMap.size) {
            // At least some file were successfully uploaded
            this.setState({
              uploaded: true,
            })
          }
          if (failedUploadsMap.size > 0) {
            throw new Error('Error during upload...')
          }
        } catch (err) {
          if (err.message) {
            console.log(err.message)
          }
          if (failedUploadsMap.size > 0) {
            // revert state file array to original, remove files that errored out
            let revertSize = 0
            const list = this.state.file.filter((f) => {
              if (!failedUploadsMap.has(f.name)) {
                revertSize += f.size
                return true
              }
              return false
            })
            this.setState({
              file: list,
              size: revertSize,
            })
          }
          this.setState({
            uploaded: false,
          })
          toastify.error(toastFileUploadError)
        } finally {
          this.setState({
            loading: false,
          })
        }
      },
    )
  }

  formatActionColumn = (cell, row) => {
    return (
      <div style={{ marginLeft: '-10px ' }}>
        <button
          className="binder-icons addldocs-action-btn"
          type="button"
          onClick={(event) => this.handleRemove(row.name, row)}
          disabled={this.state.loading}
        >
          <X size={16} />
        </button>
      </div>
    )
  }
  render() {
    let data;
    if (this.isUnderwriter) {
      data = this.state.file
    }
    else {
      data = this.state.file.filter(
        (f) => f.IsUWDoc === false
      )
    }

    return (
      <div>
        <div
          className="additional-info-row-2"
          onDrag={this.suppress}
          onDragStart={this.suppress}
          onDragOver={this.handleDragOverEnter}
          onDragEnter={this.handleDragOverEnter}
          onDragLeave={this.handleDragLeaveEnd}
          onDragEnd={this.handleDragLeaveEnd}
          onDrop={this.handleDrop}
          style={{
            width: '96.5%',
            borderRadius: '2px',
            border: '1px solid #033d67',
            paddingLeft: '20px',
          }}
        >
          <h5>
            If you have any additional submission materials, that you would like to be
            delivered directly to your Underwriter, please drag and drop to upload them
            here.
          </h5>
          <br />
          <label
            style={{
              padding: '0px 10px 5px 10px',
              margin: '-5px',
              backgroundColor: '#42b6e2',
              color: 'white',
            }}
            className="btn"
          >
            <input
              type="file"
              accept={Array.isArray(ACCEPT) ? ACCEPT.join(',') : ACCEPT}
              multiple
              style={{
                position: 'relative',
                height: '0px',
                maxWidth: '0px',
                border: 'none',
              }}
              onChange={this.handleClick}
            />
            Choose file
          </label>
          <p
            style={{
              margin: '1.5em 0 .5em 0',
              color: '#8e8e8e',
            }}
          >
            Uploaded files are not displayed after {ADDL_DOCS_EXPIRE_DAYS} days. Please
            re-upload your files if you do not see them and you are completing your
            submission after {ADDL_DOCS_EXPIRE_DAYS} days from when your files were
            uploaded.
          </p>
        </div>
        <br />
        {data.length > 0 && (
          <div style={{ width: '500px', overflowY: 'auto' }} >
            <BootstrapTable
              data={data}
              maxHeight="140px"
              maxWidth="40px"
              scrollTop={'Top'}
              bodyContainerClass="revision-table-body"
            >
              <TableHeaderColumn
                dataField="name"
                headerAlign="center"
                hover={true}
                width={'60%'}
              >
                Name
              </TableHeaderColumn>
              <TableHeaderColumn
                dataField="size"
                headerAlign="center"
                dataAlign="center"
                dataFormat={this.fileSizeHandler}
              >
                File Size
              </TableHeaderColumn>
              <TableHeaderColumn
                dataField="key"
                headerAlign="center"
                dataAlign="center"
                isKey
                dataFormat={this.formatActionColumn}
              >
                Actions
              </TableHeaderColumn>
            </BootstrapTable>
            <br />
          </div>
        )}
      </div>
    )
  }
}

export default connect()(AdditionalDocument)
